<script setup lang="ts">
	import { NuxtLink } from "#components";
	import { forEach } from "lodash-es";
	import { type MenuItem, type MenuPath, useLayoutStore } from "~/store/main";

	const props = defineProps<{
		item: MenuItem;
	}>();
	const image = markRaw(props.item.img);
	// let svgImage = null;
	// if (props.item.img) {
	// 	svgImage = await defineAsyncComponent(() => import(props.item.img));
	// }
	const { menuItems } = useLayoutStore();
	const route = useRoute();

	const isSubmenu = computed(() => "submenu" in props.item);
	const link = computed(() => {
		return "submenu" in props.item ? false : props.item.link;
	});
	const isOpen = computed(() => "submenu" in props.item && props.item.isOpen);
	const submenus = computed(() => {
		return "submenu" in props.item ? props.item.submenu : [];
	});

	const toggleSubmenu = (
		uniqueId: string,
		set: "open" | "close" | undefined = undefined,
	) => {
		const closeDescendants = (
			item: MenuPath,
			except: string | undefined = undefined,
		) => {
			forEach(item.submenu, (c) => {
				if ("submenu" in c && c.uniqueId !== except) {
					closeDescendants(c, except);
					c.isOpen = false;
				}
			});
		};

		const openParents = (items: MenuItem[], targetId: string) => {
			for (const i of items) {
				if (i.uniqueId === targetId) {
					if ("isOpen" in i) i.isOpen = true;
					return true;
				}
				if (!("submenu" in i)) continue;
				if (openParents(i.submenu, targetId)) {
					i.isOpen = true;
					return true;
				}
			}
			return false;
		};
		const findItem = (
			items: MenuItem[],
			uniqueId: string,
		): MenuItem | undefined => {
			for (const i of items) {
				if (i.uniqueId === uniqueId) {
					return i;
				}
				if ("submenu" in i) {
					const found = findItem(i.submenu, uniqueId);
					if (found) {
						return found;
					}
				}
			}
			return undefined;
		};
		const valueElement = findItem(menuItems, uniqueId);
		if (!valueElement) return;
		if ("submenu" in valueElement) {
			if ((valueElement.isOpen && set !== "open") || set === "close") {
				closeDescendants(valueElement);
				valueElement.isOpen = false;
			} else {
				forEach(menuItems, (o) => {
					if ("submenu" in o) {
						closeDescendants(o, valueElement.uniqueId);
						o.isOpen = false;
					}
				});
				openParents(menuItems, valueElement.uniqueId);
				valueElement.isOpen = true;
			}
		} else {
			forEach(menuItems, (o) => {
				if ("submenu" in o && o.uniqueId !== valueElement.uniqueId) {
					closeDescendants(o, valueElement.uniqueId);
					o.isOpen = false;
				}
			});
			if (set !== "close") {
				openParents(menuItems, valueElement.uniqueId);
			}
		}
	};

	onMounted(() => {
		watch(
			() => route.meta.uniqueId,
			() => {
				if (route.meta.uniqueId === props.item.uniqueId) {
					toggleSubmenu(props.item.uniqueId, "open");
				}
			},
		);
		if (route.meta.uniqueId === props.item.uniqueId) {
			toggleSubmenu(props.item.uniqueId, "open");
		}
	});
</script>

<template>
	<div>
		<component
			:is="isSubmenu ? 'div' : NuxtLink"
			class="group flex items-center justify-start gap-2 px-3 py-3 hover:bg-dark text-dark hover:text-white rounded-lg font-normal cursor-pointer"
			:class="{
				'bg-dark text-white':
					isOpen || route.meta.uniqueId === item.uniqueId,
			}"
			:aria-expanded="isSubmenu ? (isOpen ? 'true' : 'false') : undefined"
			:aria-label="item.title + (submenus.length ? ' (has submenu)' : '')"
			:href="link"
			tabindex="0"
			@click="toggleSubmenu(item.uniqueId)"
			@keydown.enter="toggleSubmenu(item.uniqueId)"
		>
			<component
				:is="image"
				v-if="image"
				viewBox="0 0 24 24"
				:class="{
					'stroke-white':
						isOpen || route.meta.uniqueId === item.uniqueId,
				}"
				class="stroke-dark group-hover:stroke-white w-6 h-8 flex justify-center items-center"
			></component>

			<span class="flex items-center pointer-events-none select-none">
				{{ item.title }}
			</span>
		</component>
		<div v-show="isOpen" class="pr-4 mt-2 space-y-2">
			<template v-for="(subItem, subIndex) in submenus" :key="subIndex">
				<LayoutSidebarMenuItem :item="subItem" />
			</template>
		</div>
	</div>
</template>

<style scoped></style>
